import { listingCardLcpPreloadLinks } from "~/lib/helpers/listingCardImage";
import { getCountryName } from "~/lib/helpers/constants";
import { titleCaseSlug } from "~/lib/helpers/StringHelper";
import { OFFICE_TYPES } from "~/components/blocks/Geosearch/constants";
import {
  areaMeta,
  buildAreaPageListingSubheadline,
} from "~/utils/Meta/areaMeta";

function parseQueryString(queryString: string): Record<string, string> {
  const params = new URLSearchParams(queryString);
  const result: Record<string, string> = {};
  params.forEach((value, key) => {
    result[key] = value;
  });
  return result;
}

function slugToLocationLabel(slug?: string): string | undefined {
  if (!slug) return undefined;
  const decoded = decodeURIComponent(String(slug));
  if (decoded.length <= 3) return decoded.toUpperCase();
  return titleCaseSlug(decoded).replace(/-/g, " ");
}

function resolveSpaceTypeLabelFromWorkspaceType(
  workspaceType?: string,
): string | undefined {
  if (!workspaceType || workspaceType === "All") return undefined;
  const parts = workspaceType
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length !== 1) return undefined;
  return OFFICE_TYPES.find((t) => t.value === parts[0])?.label;
}

function resolveFirstListingImage(data: any): string | undefined {
  const firstListing = data?.data?.data?.[0];
  return (
    firstListing?.assets?.[0]?.url ||
    firstListing?.assets?.[0]?.image ||
    firstListing?.imageUrl ||
    undefined
  );
}

/**
 * Synthetic area summary for location geosearch routes
 * (`/$country/$state/...`, not `/search` or `/areas`).
 * Prefer loader `locationText` when route city is missing (e.g. state-only /pk/punjab).
 */
function buildSyntheticAreaSummary(
  data: {
    params?: Record<string, string>;
    query?: string;
    locationText?: string;
    regionData?: {
      currency?: string;
      currencyIsoCode?: string;
    };
  },
  imageUrl?: string,
) {
  const params = data.params ?? {};
  const parsed = parseQueryString(data.query ?? "");
  const citySlug = params.city ? String(params.city) : "";
  const stateSlug = params.state ? String(params.state) : "";
  const region = params.country || params.region || "us";

  let city = slugToLocationLabel(citySlug);
  let state = slugToLocationLabel(stateSlug);
  const country = getCountryName(region);

  const locationFromQuery = parsed.location
    ? decodeURIComponent(String(parsed.location)).trim()
    : "";
  const locationText = data.locationText?.trim() || "";

  // Prefer loader place label (last two URL segments) when present.
  if (locationText) {
    const parts = locationText
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts[0]) city = parts[0];
    if (parts[1]) state = parts[1];
  }

  if (!city && locationFromQuery) {
    city = locationFromQuery;
  }

  if (!city && !state) {
    city = country;
  }

  const workspaceType = parsed.workspaceType
    ? String(parsed.workspaceType)
    : undefined;
  const spaceTypeFromQuery =
    resolveSpaceTypeLabelFromWorkspaceType(workspaceType);
  const spaceTypeFromRoute = params.offieType
    ? OFFICE_TYPES.find((t) => t.value === params.offieType)?.label
    : undefined;

  return {
    city,
    state,
    country,
    // Ensure Place.name has a non-empty fallback for state-only pages.
    name: city || state || country || locationText || undefined,
    spaceTypeFormula: spaceTypeFromQuery ?? spaceTypeFromRoute,
    currencyIsoCode:
      data.regionData?.currencyIsoCode ?? data.regionData?.currency,
    ...(imageUrl ? { imageUrl } : {}),
  };
}

/** Synthetic area summary for location geosearch pages (route + locationText driven). */
export function buildLocationGeosearchSyntheticAreaSummary(
  data: {
    params?: Record<string, string>;
    query?: string;
    locationText?: string;
    regionData?: {
      currency?: string;
      currencyIsoCode?: string;
    };
  },
) {
  return buildSyntheticAreaSummary(data);
}

/** Listing subheadline for location geosearch (no area profile payload). */
export function buildLocationGeosearchPageListingSubheadline(options: {
  data: {
    params?: Record<string, string>;
    query?: string;
    locationText?: string;
    regionData?: {
      currency?: string;
      currencyIsoCode?: string;
    };
    globalPriceTier?: import("~/lib/helpers/mapPinsHelper").GlobalPriceTier | null;
    mapPins?: unknown[];
    data?: {
      data?: unknown[];
      autoReCalibratedData?: unknown[];
      totalListings?: number;
      totalProperties?: number;
    };
  };
  parsedQuery?: Record<string, string | undefined>;
  filterData?: { officeTypes?: string[] };
}) {
  const listings = Array.isArray(options.data.data?.data)
    ? options.data.data.data
    : [];
  const fallbackListings = Array.isArray(
    options.data.data?.autoReCalibratedData,
  )
    ? options.data.data.autoReCalibratedData
    : [];
  const apiTotal =
    options.data.data?.totalListings ||
    options.data.data?.totalProperties ||
    0;
  const total = Math.max(
    apiTotal,
    listings.length,
    fallbackListings.length,
  );
  if (total <= 0) return null;

  const synthetic = buildSyntheticAreaSummary(options.data);
  const paramCity = options.parsedQuery?.city
    ? String(options.parsedQuery.city)
    : "";
  const paramState = options.parsedQuery?.state
    ? String(options.parsedQuery.state)
    : "";
  const paramCountry = options.parsedQuery?.country
    ? String(options.parsedQuery.country).toLowerCase()
    : "";
  const routeCountry = options.data.params?.country;
  const hasAnyLocationParams = Boolean(
    paramCountry ||
    paramState ||
    paramCity ||
    options.data.params?.city ||
    options.data.params?.state ||
    options.data.params?.country,
  );

  const subheadline = buildAreaPageListingSubheadline({
    areaSummary: synthetic,
    totalProperties: total,
    listings: listings as any[],
    fallbackListings: fallbackListings as any[],
    regionCurrency:
      options.data.regionData?.currencyIsoCode ??
      options.data.regionData?.currency,
    paramCity,
    paramState,
    paramCountry,
    areaCity: synthetic.city,
    areaState: synthetic.state,
    areaCountry: synthetic.country,
    routeCountry,
    hasAnyLocationParams,
    selectedOfficeTypes: options.filterData?.officeTypes,
    areaSpaceTypeFormula: synthetic.spaceTypeFormula,
    workspaceType: options.parsedQuery?.workspaceType,
    officeTypeFilterTouched:
      options.parsedQuery?.officeTypeFilterTouched === "1",
    placeOverride: options.data.locationText?.trim() || undefined,
    globalPriceTier: options.data.globalPriceTier,
    mapPins: options.data.mapPins,
  });

  return subheadline;
}

export function locationGeosearchMeta(data: any, path: string, search?: string) {
  const firstImage = resolveFirstListingImage(data);
  const syntheticAreaSummary = buildSyntheticAreaSummary(data, firstImage);
  const listingMeta = areaMeta({
    data: {
      regionData: data.regionData,
      areaSummary: syntheticAreaSummary,
      data: data.data,
      globalPriceTier: data.globalPriceTier,
      mapPins: data.mapPins,
    },
    path,
    search,
  })
    // The location page renders the visible <Breadcrumbs> component, which already
    // emits a single canonical BreadcrumbList JSON-LD matching the on-page trail
    // (Home > Country > State > City). Drop the area-page breadcrumb schema here to
    // avoid emitting a second/duplicate BreadcrumbList (SEO: exactly one per page).
    .filter((tag: any) => {
      const ld = tag?.["script:ld+json"];
      return !(ld && ld["@type"] === "BreadcrumbList");
    });

  const preloadLinks = listingCardLcpPreloadLinks(firstImage);

  return [
    ...preloadLinks,
    ...listingMeta,
  ];
}
